// src/ProtectedMembersPage.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate, useLocation } from 'react-router-dom';
import { ACCESS, memberAccess } from './membership.js';
import { clearSsoAttempt, lastSsoAttempt, markSsoAttempt, silentSignInPlan } from './silentSignIn.js';
import { isSigningOut } from './signOut.js';
import './ProtectedMembersPage.css';

// /members route guard. UX ONLY: it decides which screen to show from
// custom:isSubscribed in the ID token. It is not the security control - the
// members data API verifies the ID token and the same attribute server-side.
//
//   member      -> Portfolio Intelligence (children)
//   non-member  -> /subscribe (signed in, custom:isSubscribed is not "true")
//   signed out  -> first, for a returning user whose stored session expired,
//                  one silent attempt to restore it (silentSignIn.js); then
//                  /signup. A first-time or signed-out visitor goes straight
//                  to /signup.

const Status = ({ text }) => (
  <div className="protected-page-container">
    <div className="loading-container">
      <div className="spinner-card" role="status">
        <div className="spinner" aria-hidden="true"></div>
        <p className="loading-text">{text}</p>
      </div>
    </div>
  </div>
);

const ProtectedMembersPage = ({ children }) => {
  const auth = useAuth();
  const location = useLocation();
  const access = memberAccess(auth);
  // idle -> restoring -> (signed in) | gave-up
  const [restore, setRestore] = useState('idle');
  const attemptStarted = useRef(false);

  useEffect(() => {
    if (access === ACCESS.MEMBER || access === ACCESS.NON_MEMBER) {
      // Signed in: forget earlier attempts so a later expiry may try again.
      clearSsoAttempt();
      attemptStarted.current = false;
      setRestore('idle');
      return;
    }
    if (access !== ACCESS.SIGNED_OUT || attemptStarted.current) return;
    attemptStarted.current = true;

    const lastAttemptAt = lastSsoAttempt();
    const plan = isSigningOut()
      ? 'none'
      : silentSignInPlan({ user: auth.user, lastAttemptAt, now: Date.now() });
    if (plan === 'none' || !markSsoAttempt()) {
      // Back from a failed attempt with the dead expired session still
      // stored: remove it, so later visits are treated as signed out.
      if (auth.user && lastAttemptAt !== null && !isSigningOut()) auth.removeUser().catch(() => {});
      setRestore('gave-up');
      return;
    }
    setRestore('restoring');

    // Leaves the page; comes back via the Cognito callback either signed in
    // or with login_required, when the marker sends this tab to /signup.
    const trySso = () => auth.signinRedirect({ prompt: 'none' });
    const attempt = plan === 'refresh'
      ? auth.signinSilent().then((user) => {
        if (!user) throw new Error('no session after refresh');
      }).catch(trySso)
      : trySso();
    Promise.resolve(attempt).catch(() => setRestore('gave-up'));
  }, [access, auth]);

  if (access === ACCESS.LOADING) return <Status text="Checking your membership..." />;
  if (access === ACCESS.NON_MEMBER) return <Navigate to="/subscribe" replace state={{ from: location }} />;
  if (access === ACCESS.MEMBER) return <>{children}</>;

  if (restore === 'gave-up') return <Navigate to="/signup" replace state={{ from: location }} />;
  return <Status text="Restoring your session..." />;
};

export default ProtectedMembersPage;
